import { Directive, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { NgControl } from '@angular/forms';

import { Subject, takeUntil } from 'rxjs';

/**
 * Directive to add 'p-filled' class to the inner input element of p-inputMask
 * when it has a value. This is required for p-floatlabel variant="on" to work correctly.
 * 
 * **TEMPORARY FIX**: This directive is a workaround for PrimeNG v18 issue where p-inputMask
 * doesn't automatically add the p-filled class. This issue was fixed in PrimeNG v20.
 * 
 * **TODO**: Remove this directive when upgrading to PrimeNG v20 or later.
 * 
 * Usage: Automatically applied to all p-inputMask elements with form bindings
 * <p-inputMask formControlName="field" />
 */
// eslint-disable-next-line @angular-eslint/directive-selector
@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'p-inputMask[formControlName], p-inputMask[formControl], p-inputMask[ngModel]',
    standalone: true
})
export class InputMaskFilledDirective implements OnInit, OnDestroy {
    private _destroy$ = new Subject<void>();
    private _inputElement: HTMLInputElement | null = null;

    constructor(
        private el: ElementRef,
        private control: NgControl
    ) { }

    public ngOnInit(): void {
        // Find the inner input element
        this._inputElement = this.el.nativeElement.querySelector('input');

        if (!this._inputElement || !this.control.control) {
            return;
        }

        // Update filled class on value changes
        this.control.control.valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
                this._updateFilledClass();
            });

        // Initial check
        this._updateFilledClass();

        // Also listen to blur events to handle manual input
        this._inputElement.addEventListener('blur', () => this._updateFilledClass());
        this._inputElement.addEventListener('input', () => this._updateFilledClass());
    }

    private _updateFilledClass(): void {
        if (!this._inputElement) {
            return;
        }

        const value = this.control.control?.value;
        const hasValue = value != null && value !== '';

        if (hasValue) {
            this._inputElement.classList.add('p-filled');
        } else {
            this._inputElement.classList.remove('p-filled');
        }
    }

    public ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }
}
